const ACTION_BASE_RECOVERY = {
  assign_driver: 76,
  move_store: 82,
  swap_product: 64,
  change_payment: 52,
  pause_order: 18,
  cancel_order: 5,
  monitor: 35,
};

function safeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function firstText(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function actionType(suggestion = {}) {
  if (suggestion.type) return suggestion.type;
  if (suggestion.actionKey === 'assign') return 'assign_driver';
  if (suggestion.actionKey === 'move') return 'move_store';
  if (suggestion.actionKey === 'swap') return 'swap_product';
  if (suggestion.actionKey === 'payment') return 'change_payment';
  if (suggestion.actionKey === 'pause') return 'pause_order';
  if (suggestion.actionKey === 'cancel') return 'cancel_order';
  return 'monitor';
}

function storeLoad(storeSignal = {}) {
  return Number(storeSignal.loadScore ?? storeSignal.riskScore ?? storeSignal.load ?? 0) || 0;
}

function storeName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return firstText(order.store, order.storeName, raw.store, raw.storeName, raw.site, order.site);
}

function targetStoreName(suggestion = {}) {
  return firstText(suggestion.payload?.targetStore, suggestion.targetStore);
}

function actionRecovery({ suggestion, prediction, currentStoreSignal, targetStoreSignal, driverSignals }) {
  const type = actionType(suggestion);
  let recovery = ACTION_BASE_RECOVERY[type] ?? 45;
  const predictedRisk = prediction?.predictedRisk || 'healthy';
  const minutesToBreach = Number(prediction?.minutesToBreach ?? 30);
  const currentLoad = storeLoad(currentStoreSignal);
  const targetLoad = storeLoad(targetStoreSignal);
  const availableDrivers = safeArray(driverSignals).filter((driver) => driver.available || (driver.online && Number(driver.activeRuns || 0) === 0)).length;

  if (predictedRisk === 'critical') recovery += 6;
  if (minutesToBreach <= 0) recovery -= 14;
  else if (minutesToBreach <= 10) recovery += 4;

  if (type === 'assign_driver') {
    recovery += Math.min(12, availableDrivers * 4);
    if (prediction?.driverRisk) recovery += 10;
  }

  if (type === 'move_store') {
    const loadDrop = currentLoad - targetLoad;
    recovery += clamp(Math.round(loadDrop / 4), -10, 18);
  }

  if (type === 'swap_product' && suggestion.severity === 'critical') recovery += 8;

  return clamp(Math.round(recovery), 5, 96);
}

function networkImpact({ suggestion, prediction, currentStoreSignal, targetStoreSignal }) {
  const type = actionType(suggestion);
  const currentStore = currentStoreSignal?.store || 'Current store';
  const targetStore = targetStoreSignal?.store || targetStoreName(suggestion) || 'Target store';
  const currentLoad = storeLoad(currentStoreSignal);
  const targetLoad = storeLoad(targetStoreSignal);

  if (type === 'move_store') {
    return {
      primary: `${currentStore} load ${currentLoad}% → ${Math.max(0, currentLoad - 12)}%`,
      secondary: `${targetStore} load ${targetLoad}% → ${Math.min(100, targetLoad + 9)}%`,
      outcome: 'Queue pressure reduced before SLA risk spreads.',
    };
  }

  if (type === 'assign_driver') {
    return {
      primary: 'Driver gap closed for selected order',
      secondary: prediction?.minutesToBreach <= 0 ? 'Late order recovery begins immediately' : `Protects SLA window: ${prediction?.breachLabel || 'active'}`,
      outcome: 'Customer waiting time stops compounding.',
    };
  }

  if (type === 'swap_product') {
    return {
      primary: 'Product decision removed from operator backlog',
      secondary: targetStore ? `${targetStore} remains the backup store signal` : 'Backup store signal stays available',
      outcome: 'Order can continue without leaving Control Tower.',
    };
  }

  return {
    primary: 'No major network load change predicted',
    secondary: prediction?.summary || 'Monitor order movement',
    outcome: 'Keeps the operator informed while live signals update.',
  };
}

export function rankRecoveryActions({ order, suggestions = [], prediction, storeSignals = [], driverSignals = [] } = {}) {
  const currentStore = storeName(order);
  const currentStoreSignal = safeArray(storeSignals).find((store) => store.store === currentStore);

  return safeArray(suggestions)
    .map((suggestion) => {
      const target = targetStoreName(suggestion);
      const targetStoreSignal = safeArray(storeSignals).find((store) => store.store === target);
      const recoveryChance = actionRecovery({ suggestion, prediction, currentStoreSignal, targetStoreSignal, driverSignals });
      const basePriority = Number(suggestion.priority ?? 0) || 0;
      const actionScore = clamp(Math.round((basePriority * 0.45) + (recoveryChance * 0.55)), 0, 100);
      return {
        ...suggestion,
        recoveryChance,
        actionScore,
        recoveryLabel: `${recoveryChance}% recovery`,
        bestAction: false,
        networkImpact: networkImpact({ suggestion, prediction, currentStoreSignal, targetStoreSignal }),
      };
    })
    .sort((a, b) => b.actionScore - a.actionScore || b.recoveryChance - a.recoveryChance)
    .map((suggestion, index) => ({ ...suggestion, rank: index + 1, bestAction: index === 0 }));
}

export function summarizeBestRecoveryAction(actions = []) {
  const best = safeArray(actions)[0];
  if (!best) {
    return {
      title: 'No recovery action available',
      recoveryChance: 0,
      label: 'Waiting for stronger live signals',
    };
  }

  return {
    title: best.actionLabel || best.action || best.title,
    recoveryChance: best.recoveryChance,
    label: `${best.recoveryChance}% expected recovery`,
    type: actionType(best),
  };
}
